// Which catalogue title a process is — the station's matcher (station/Core/Library/Play/TitleProcessMatcher.cs), in
// TypeScript for HQ's detection testing tool (F-HQ-16, F-CAT-04; spec/10 §7.3, spec/14 §6). HQ pastes a process list
// (names or full image paths, one per line) and sees which titles would match and by which rule, before a station ever
// runs the title.
//
// The same rules, in the same order, as the station applies them to a title's bundle entry:
//
//   catalogue  the title's own detection rules. Every rule it sets must hold: the process name (lower case, `.exe`
//              stripped on both sides), the path pattern (a wildcard over the full image path, `*` any run and `?` one
//              character) and the regex (case-insensitive, over the image path).
//   recipe     a title with no rules at all falls back to the file names its `exe` recipes start — never a launcher's
//              own executable (steam.exe, RiotClientServices.exe …) nor a bootstrapper (update, launcher, setup).
//   default    the curated default names (`DEFAULT_TITLE_DETECTIONS`) for a well-known title whose rules are empty —
//              HQ's own rules win.
//
// Two differences, both because a server cannot see a PC: path variables in a pattern (`{SteamLibrary}`,
// `{ProgramFiles}`, `%ProgramFiles%` …) are resolved on the PC, so here each one matches any run of characters; and the
// install-derived evidence (Steam app manifests, Epic manifests) and Steam's running app id are a PC's own, so they are
// not part of a paste. Pure: no I/O, no Zod — safe for any bundle.
import { defaultDetectionFor } from "./default-process-names.js";
/** How a process was matched to a title: its own rules, its exe recipe's file name, or the curated defaults. */
export const DETECTION_TEST_VIAS = ['catalogue', 'recipe', 'default'];
/** Which rule held. A `catalogue` match lists every rule the title sets (all must hold); `recipe`/`default` name theirs. */
export const DETECTION_TEST_RULES = ['processName', 'pathPattern', 'regex'];
/** The most lines a paste may carry — a busy PC runs a few hundred processes. */
export const MAX_DETECTION_TEST_LINES = 1000;
/** A process or rule name as compared: trimmed, lower case, without a trailing `.exe` (the station's `ProcessName`). */
export function comparableProcessName(name) {
    const lower = (name ?? '').trim().toLowerCase();
    return lower.endsWith('.exe') ? lower.slice(0, -4) : lower;
}
/**
 * The processes in a pasted list: one per non-empty line. A line may be a bare name (`cs2.exe`), a full image path
 * (`D:\SteamLibrary\steamapps\common\Counter-Strike Global Offensive\game\bin\win64\cs2.exe`), or a `tasklist /fo csv`
 * row (`"cs2.exe","1234",…` — its first field is taken). Quotes around a line are dropped. At most
 * `MAX_DETECTION_TEST_LINES`.
 */
export function parseProcessList(text) {
    const out = [];
    for (const raw of text.split(/\r?\n/)) {
        let line = raw.trim();
        if (line === '')
            continue;
        if (line.startsWith('"') && line.includes('","'))
            line = line.slice(1, line.indexOf('","'));
        line = line.replace(/^"+|"+$/g, '').trim();
        if (line === '')
            continue;
        const slash = Math.max(line.lastIndexOf('\\'), line.lastIndexOf('/'));
        const isPath = slash >= 0;
        const name = isPath ? line.slice(slash + 1) : line;
        if (name === '')
            continue;
        out.push({ input: line, name, path: isPath ? line : null });
        if (out.length >= MAX_DETECTION_TEST_LINES)
            break;
    }
    return out;
}
/** A launcher's own executables — never a title (the station's `InstallDetector.LauncherOfExecutable`). */
const LAUNCHER_EXECUTABLES = new Set([
    'steam',
    'epicgameslauncher',
    'riotclientservices',
    'riotclientux',
    'battle.net',
    'battle.net launcher',
    'eadesktop',
    'origin',
    'upc',
    'ubisoftconnect',
    'uplay',
]);
/** Executables that start something else and are never the title itself (the station's `NeverTheTitle`). */
const NEVER_THE_TITLE = new Set(['update', 'launcher', 'setup', 'unins000']);
/** A path as the station compares it: forward slashes as backslashes, repeated separators folded. */
function normalisePath(path) {
    return path.trim().replaceAll('/', '\\').replace(/\\{2,}/g, '\\');
}
/** A compiled case-insensitive regex, or undefined when it does not compile (then it matches nothing, as on the station). */
function safeRegex(pattern) {
    try {
        return new RegExp(pattern, 'i');
    }
    catch {
        return undefined;
    }
}
/** The wildcard path pattern as a regex: `*` any run, `?` one character, a path variable any run. Anchored, case-insensitive. */
export function pathPatternRegex(pattern) {
    const withVariables = normalisePath(pattern).replace(/\{[^}]+\}|%[^%]+%/g, '\u0000');
    let source = '^';
    for (const ch of withVariables) {
        if (ch === '\u0000' || ch === '*')
            source += '.*';
        else if (ch === '?')
            source += '.';
        else
            source += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return safeRegex(`${source}$`);
}
/** The file name a Squirrel `Update.exe --processStart Discord.exe` recipe starts, or the recipe path's file name. */
function recipeProgram(recipe) {
    const started = /--processStart\s+"?(?<exe>[^"\s]+)/i.exec(recipe.args ?? '');
    if (started?.groups?.['exe'] !== undefined)
        return started.groups['exe'];
    const path = recipe.path ?? '';
    return path.slice(Math.max(path.lastIndexOf('\\'), path.lastIndexOf('/')) + 1);
}
/**
 * How `process` is `title`, or undefined when it is not — the station's rules in the station's order: the title's own
 * rules (or, with none, its exe recipes' file names), then the curated defaults for a title with no rules at all.
 */
export function matchTitleDetection(title, process) {
    const name = comparableProcessName(process.name);
    const path = process.path === null ? null : normalisePath(process.path);
    const names = new Set(title.detection.processNames.map(comparableProcessName).filter((value) => value !== ''));
    const pattern = title.detection.pathPattern?.trim() ?? '';
    const regex = title.detection.regex?.trim() ?? '';
    const hasRules = names.size > 0 || pattern !== '' || regex !== '';
    if (hasRules) {
        const rules = [];
        if (names.size > 0) {
            if (!names.has(name))
                return undefined;
            rules.push('processName');
        }
        if (pattern !== '') {
            const compiled = pathPatternRegex(pattern);
            if (path === null || compiled === undefined || !compiled.test(path))
                return undefined;
            rules.push('pathPattern');
        }
        if (regex !== '') {
            const compiled = safeRegex(regex);
            if (path === null || compiled === undefined || !compiled.test(path))
                return undefined;
            rules.push('regex');
        }
        return { via: 'catalogue', rules };
    }
    // No rules: the program its exe recipes start, never a launcher's own executable or a bootstrapper.
    for (const recipe of title.launchRecipes) {
        if (recipe.type !== 'exe')
            continue;
        const program = comparableProcessName(recipeProgram(recipe));
        const recipeFile = comparableProcessName(recipeProgram({ type: 'exe', path: recipe.path }));
        if (program === '' || NEVER_THE_TITLE.has(program) || LAUNCHER_EXECUTABLES.has(recipeFile))
            continue;
        if (program === name)
            return { via: 'recipe', rules: ['processName'] };
    }
    const defaults = defaultDetectionFor(title.name);
    if (defaults !== undefined && defaults.processNames.map(comparableProcessName).includes(name)) {
        if (defaults.regex === undefined)
            return { via: 'default', rules: ['processName'] };
        const compiled = safeRegex(defaults.regex);
        if (path !== null && compiled !== undefined && compiled.test(path))
            return { via: 'default', rules: ['processName', 'regex'] };
    }
    return undefined;
}
//# sourceMappingURL=title-detection-match.js.map